import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Badge,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  useToast,
} from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axiosInstance from "../../routes/axiosInstance";
import { getFingerprintId } from "../../utils/fingerprint";
import { FaUserSecret, FaUser } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

const SpacePreview = ({ onJoinGuest, onLogin }) => {
  const { currentUser } = useAuth();
  const { referenceId, spaceNameSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();
  const [alias, setAlias] = useState("");
  const [aliasError, setAliasError] = useState("");
  const [manualGuestAccessId, setManualGuestAccessId] = useState("");
  const [isGuestModalOpen, setGuestModalOpen] = useState(false);
  const [isAccessModalOpen, setAccessModalOpen] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);

  // Reusable footer component
  const renderFooter = () => (
    <Box py={4} textAlign="center">
      <Text
        as="a"
        href={window.location.origin}
        target="_blank"
        rel="noopener noreferrer"
        fontSize="sm"
        color="gray.700"
        fontWeight="medium"
        cursor="pointer"
        _hover={{ textDecoration: "underline", color: "gray.600" }}
        bg="rgba(255,255,255,0.6)"
        px={3}
        py={1}
        borderRadius="md"
        display="inline-block"
      >
        Powered by VartaVerse
      </Text>
    </Box>
  );

  const storeMemberAndRedirect = useCallback(
    (newMemberData) => {
      localStorage.setItem(
        `member_${referenceId}`,
        JSON.stringify({
          member: newMemberData,
          guestAccessId: newMemberData.guestAccessId,
          alias: newMemberData.alias,
          joinedAt: newMemberData.joinedAt,
        }),
      );
      navigate(`/space/${referenceId}/${spaceNameSlug}`);
    },
    [referenceId, spaceNameSlug, navigate],
  );

  useEffect(() => {
    const storeMemberAndRedirectLocal = (newMemberData) => {
      localStorage.setItem(
        `member_${referenceId}`,
        JSON.stringify({
          member: newMemberData,
          guestAccessId: newMemberData.guestAccessId,
          alias: newMemberData.alias,
          joinedAt: newMemberData.joinedAt,
        }),
      );
      navigate(`/space/${referenceId}/${spaceNameSlug}`);
    };

    const validateAndFetch = async () => {
      if (referenceId) {
        const localMember = localStorage.getItem(`member_${referenceId}`);
        if (localMember) {
          try {
            const memberObj = JSON.parse(localMember);
            const res = await axiosInstance.get(
              `/api/spaces/${referenceId}/validate-access`,
              {
                headers: {
                  Authorization: `Guest ${memberObj.guestAccessId}`,
                  "Content-type": "application/json",
                },
              },
            );
            if (res.status === 200) {
              storeMemberAndRedirectLocal(res.data?.data);
              return;
            } else {
              localStorage.removeItem(`member_${referenceId}`);
            }
          } catch (err) {
            if (err.response?.status === 401) {
              localStorage.removeItem(`member_${referenceId}`);
            }
          }
        }
        // If not valid or not present, fetch space preview
        fetchSpace();
      }
    };

    const fetchSpace = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/spaces/${referenceId}/preview`);
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error("Channel not found or has been removed!");
          } else if (res.status >= 500) {
            throw new Error("Server error. Please try again later");
          } else {
            throw new Error("Failed to load space details");
          }
        }
        const data = await res.json();

        // Check if the API returned valid data
        if (!data || !data.data) {
          throw new Error("No space data received");
        }

        setSpace(data.data);
      } catch (err) {
        console.error("Error fetching space:", err);
        setError(err.message || "Unable to load space details");
      } finally {
        setLoading(false);
      }
    };
    validateAndFetch();
  }, [referenceId, spaceNameSlug, navigate]);

  const handleManualGuestLoginWithGid = useCallback(
    async (guestAccessId) => {
      if (!guestAccessId?.trim()) {
        return;
      }
      try {
        setAccessLoading(true);
        const res = await axiosInstance.get(
          `/api/spaces/${referenceId}/validate-access`,
          {
            headers: {
              Authorization: `Guest ${guestAccessId.trim()}`,
              "Content-type": "application/json",
            },
          },
        );

        if (res.status === 200) {
          const memberData = res.data?.data;
          if (!memberData) {
            throw new Error("No member data received from server");
          }
          storeMemberAndRedirect(memberData);
        } else {
          throw new Error("Invalid Guest Access ID");
        }
      } catch (err) {
        console.error("Error validating guest access:", err);
        let errorMessage = "Failed to validate Guest Access ID";

        if (err.response?.status === 404) {
          errorMessage = "Space not found or access denied";
        } else if (
          err.response?.status === 401 ||
          err.response?.status === 403
        ) {
          errorMessage = "Invalid or expired Guest Access ID";
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }

        toast({
          title: "Access Failed",
          description: errorMessage,
          status: "error",
          duration: 4000,
        });
      } finally {
        setAccessLoading(false);
      }
    },
    [referenceId, toast, storeMemberAndRedirect],
  );

  // Handle gid parameter from URL
  useEffect(() => {
    const gid = searchParams.get("gid");
    if (gid && space && !loading) {
      setManualGuestAccessId(gid);
      // Automatically trigger guest login with the provided gid
      handleManualGuestLoginWithGid(gid);
    }
  }, [searchParams, space, loading, handleManualGuestLoginWithGid]);

  const handleLogin = () => {
    navigate("/login");
  };

  const handleAuthenticatedUserJoin = () => {
    // If user is authenticated, redirect directly to space
    navigate(`/space/${referenceId}/${spaceNameSlug}`);
  };

  const openGuestModal = () => {
    setAliasError("");
    setGuestModalOpen(true);
  };
  const closeGuestModal = () => {
    setAliasError("");
    setGuestModalOpen(false);
  };
  const openAccessModal = () => setAccessModalOpen(true);
  const closeAccessModal = () => setAccessModalOpen(false);

  const validateAlias = (aliasValue) => {
    const trimmedAlias = aliasValue.trim();

    // Check if alias is empty
    if (!trimmedAlias) {
      return "Alias is required";
    }

    // Check length (3-20 characters)
    if (trimmedAlias.length < 3) {
      return "Alias must be at least 3 characters long";
    }

    if (trimmedAlias.length > 20) {
      return "Alias must be 20 characters or less";
    }

    // Check for allowed characters (alphanumeric, @, and single spaces between words)
    const allowedPattern = /^[a-zA-Z0-9@]+(\s[a-zA-Z0-9@]+)*$/;
    if (!allowedPattern.test(trimmedAlias)) {
      return "Alias can only contain letters, numbers";
    }

    // Check for consecutive spaces or leading/trailing spaces (already handled by trim, but double-check)
    if (trimmedAlias.includes("  ")) {
      return "Multiple consecutive spaces are not allowed";
    }

    return null; // Valid alias
  };

  const handleJoin = async () => {
    const validationError = validateAlias(alias);
    if (validationError) {
      toast({
        title: validationError,
        status: "warning",
        duration: 3000,
      });
      return;
    }
    try {
      setJoinLoading(true);
      const deviceFingerprintId = await getFingerprintId();

      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Guest `,
        },
      };

      const createChatPayload = {
        alias: alias.trim(),
        deviceId: deviceFingerprintId,
      };
      const newMember = await axiosInstance.post(
        `/api/spaces/${referenceId}/join`,
        createChatPayload,
        config,
      );

      if (newMember.status === 201 || newMember.status === 200) {
        const newMemberData = newMember.data?.data;
        if (!newMemberData) {
          throw new Error("No member data received from server");
        }
        closeGuestModal();
        storeMemberAndRedirect(newMemberData);
      } else {
        throw new Error("Failed to join space");
      }
    } catch (err) {
      console.error("Error joining space:", err);
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to join space";
      toast({
        title: "Join Failed",
        description: errorMessage,
        status: "error",
        duration: 4000,
      });
    } finally {
      setJoinLoading(false);
    }
  };

  const handleManualGuestLogin = async () => {
    if (!manualGuestAccessId.trim()) {
      toast({
        title: "Guest Access ID is required",
        status: "warning",
        duration: 2000,
      });
      return;
    }
    try {
      setAccessLoading(true);
      const res = await axiosInstance.get(
        `/api/spaces/${referenceId}/validate-access`,
        {
          headers: {
            Authorization: `Guest ${manualGuestAccessId.trim()}`,
            "Content-type": "application/json",
          },
        },
      );

      if (res.status === 200) {
        const memberData = res.data?.data;
        if (!memberData) {
          throw new Error("No member data received from server");
        }
        closeAccessModal();
        storeMemberAndRedirect(memberData);
      } else {
        throw new Error("Invalid Guest Access ID");
      }
    } catch (err) {
      console.error("Error validating guest access:", err);
      let errorMessage = "Failed to validate Guest Access ID";

      if (err.response?.status === 404) {
        errorMessage = "Space not found or access denied";
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = "Invalid or expired Guest Access ID";
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      toast({
        title: "Access Failed",
        description: errorMessage,
        status: "error",
        duration: 4000,
      });
    } finally {
      setAccessLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex
        direction="column"
        minH="100vh"
        bgGradient="linear(to-br, teal.600, white, gray.50)"
      >
        <Flex align="center" justify="center" flex="1">
          <Box
            p={8}
            maxW="md"
            w="100%"
            bg="white"
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="md"
            textAlign="center"
          >
            <Text fontSize="lg" mb={4}>
              Loading space details...
            </Text>
            <Text fontSize="sm" color="gray.500">
              Please wait while we fetch the space information
            </Text>
          </Box>
        </Flex>

        {/* Footer at bottom of page */}
        {renderFooter()}
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex
        direction="column"
        minH="100vh"
        bgGradient="linear(to-br, teal.600, white, gray.50)"
      >
        <Flex align="center" justify="center" flex="1">
          <Box
            p={8}
            maxW="md"
            w="100%"
            bg="white"
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="md"
            textAlign="center"
          >
            <Heading size="md" color="red.500" mb={4}>
              Oops! Something went wrong
            </Heading>
            <Text color="gray.600" mb={6}>
              {error}
            </Text>
            <Stack spacing={3}>
              <Button
                colorScheme="teal"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate("/")}>
                Go Home
              </Button>
            </Stack>
          </Box>
        </Flex>

        {/* Footer at bottom of page */}
        {renderFooter()}
      </Flex>
    );
  }

  if (!space) {
    return (
      <Flex
        direction="column"
        minH="100vh"
        bgGradient="linear(to-br, teal.600, white, gray.50)"
      >
        <Flex align="center" justify="center" flex="1">
          <Box
            p={8}
            maxW="md"
            w="100%"
            bg="white"
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="md"
            textAlign="center"
          >
            <Heading size="md" color="orange.500" mb={4}>
              Space Not Available
            </Heading>
            <Text color="gray.600" mb={6}>
              The space you're looking for doesn't exist or is no longer
              available.
            </Text>
            <Button colorScheme="teal" onClick={() => navigate("/")}>
              Go Home
            </Button>
          </Box>
        </Flex>

        {/* Footer at bottom of page */}
        {renderFooter()}
      </Flex>
    );
  }

  const {
    title,
    kind,
    admin,
    isIncognito,
    expiresAt,
    memberCount,
    description,
  } = space;
  const timeRemaining = expiresAt
    ? formatDistanceToNow(new Date(expiresAt), { addSuffix: true })
    : null;

  return (
    <>
      <Flex
        direction="column"
        minH="100vh"
        bgGradient="linear(to-br, teal.600, white, gray.50)"
      >
        <Flex align="center" justify="center" flex="1">
          <Box
            p={8}
            maxW="lg"
            w="100%"
            bg="white"
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="md"
          >
            <Heading mb={6} size="lg" textAlign="center">
              {title}
            </Heading>

            {description && (
              <Box mb={4} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.700" textAlign="center">
                  {description}
                </Text>
              </Box>
            )}

            <Stack spacing={4} mb={6}>
              <Flex align="center">
                <Text fontWeight="bold">Type:</Text>
                <Badge ml={2} colorScheme="purple" fontSize="0.9em">
                  {kind}
                </Badge>
              </Flex>

              <Flex align="center">
                <Text fontWeight="bold">Admin:</Text>
                <Text ml={2} display="flex" alignItems="center">
                  {admin}
                  {isIncognito ? (
                    <FaUserSecret style={{ marginLeft: 8 }} title="Incognito" />
                  ) : (
                    <FaUser style={{ marginLeft: 8 }} title="Admin" />
                  )}
                </Text>
              </Flex>

              {expiresAt && (
                <Flex align="center">
                  <Text fontWeight="bold">Expires:</Text>
                  <Text ml={2}>{timeRemaining}</Text>
                </Flex>
              )}

              <Flex align="center">
                <Text fontWeight="bold">Members:</Text>
                <Text ml={2}>{memberCount}</Text>
              </Flex>
            </Stack>

            <Flex justify="space-between">
              {(space.participantMode === "ANONYMOUS" ||
                space.participantMode === "ALL") && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="teal"
                    onClick={openGuestModal}
                    flex="1"
                    mr={space.participantMode === "ALL" ? 2 : 0}
                  >
                    Continue as Guest
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    onClick={openAccessModal}
                    flex="1"
                    ml={2}
                  >
                    Have Access Code?
                  </Button>
                </>
              )}
              {(space.participantMode === "AUTHENTICATED" ||
                space.participantMode === "ALL") && (
                <Button
                  size="sm"
                  colorScheme="teal"
                  onClick={
                    currentUser && currentUser.id && currentUser.name
                      ? handleAuthenticatedUserJoin
                      : handleLogin
                  }
                  flex="1"
                  ml={space.participantMode === "ALL" ? 2 : 0}
                >
                  {currentUser && currentUser.id && currentUser.name
                    ? `Continue as ${currentUser.name}`
                    : "Login to Join"}
                </Button>
              )}
            </Flex>
          </Box>
        </Flex>

        {/* Footer at bottom of page */}
        {renderFooter()}
      </Flex>

      {/* Guest Join Modal */}
      <Modal
        closeOnOverlayClick={false}
        isOpen={isGuestModalOpen}
        onClose={closeGuestModal}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Join as Guest</ModalHeader>
          <ModalBody>
            <FormControl isRequired mb={4} isInvalid={!!aliasError}>
              <FormLabel>Nickname</FormLabel>
              <Input
                placeholder="Enter a nickname"
                value={alias}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setAlias(newValue);
                  // Real-time validation
                  const error = validateAlias(newValue);
                  setAliasError(error || "");
                }}
                isDisabled={joinLoading}
              />
              {aliasError && <FormErrorMessage>{aliasError}</FormErrorMessage>}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="ghost"
              mr={3}
              onClick={closeGuestModal}
              isDisabled={joinLoading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="teal"
              isLoading={joinLoading}
              loadingText="Joining..."
              isDisabled={!!aliasError}
              onClick={async () => {
                const validationError = validateAlias(alias);
                if (validationError) {
                  toast({
                    title: validationError,
                    status: "warning",
                    duration: 3000,
                  });
                  return;
                }
                await handleJoin();
              }}
            >
              Join
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Access Code Modal */}
      <Modal
        closeOnOverlayClick={false}
        isOpen={isAccessModalOpen}
        onClose={closeAccessModal}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Login with Guest Access Code</ModalHeader>
          <ModalBody>
            <FormControl isRequired mb={2}>
              <FormLabel>Guest Access ID</FormLabel>
              <Input
                placeholder="Paste your Guest Access ID"
                value={manualGuestAccessId}
                onChange={(e) => setManualGuestAccessId(e.target.value)}
                isDisabled={accessLoading}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="ghost"
              mr={3}
              onClick={closeAccessModal}
              isDisabled={accessLoading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="purple"
              isLoading={accessLoading}
              loadingText="Validating..."
              onClick={async () => {
                if (!manualGuestAccessId.trim()) {
                  toast({
                    title: "Guest Access ID is required",
                    status: "warning",
                    duration: 2000,
                  });
                  return;
                }
                await handleManualGuestLogin();
              }}
            >
              Login with Access ID
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SpacePreview;
